import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Play, Pause, Clock } from 'lucide-react';
import {
  Card, Button, Input, Select, Label, Badge, EmptyState, Modal, Textarea, KPI,
  SkeletonListRows,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import {
  listTimeEntries, createTimeEntry, listMatters,
} from '../lib/db';
import { formatMoney, formatDuration, formatDate } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';

const blank = {
  matter_id: '', description: '', entry_date: new Date().toISOString().slice(0,10),
  duration_minutes: 6, hourly_rate: 250, billable: true, activity_code: '',
};

export default function TimePage() {
  const { firmId, refreshSignal } = useFirm();
  const { userProfile } = useAuth();
  const log = useActivityLogger();
  const [entries, setEntries] = useState([]);
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  // Timer
  const [timer, setTimer] = useState({ running: false, startedAt: null, elapsed: 0, matter_id: '', description: '' });
  useEffect(() => {
    if (!timer.running) return;
    const id = setInterval(() => setTimer((t) => ({ ...t, elapsed: Date.now() - t.startedAt })), 1000);
    return () => clearInterval(id);
  }, [timer.running]);

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    (async () => {
        try {
      const [{ data: t }, { data: m }] = await Promise.all([
        listTimeEntries(firmId), listMatters(firmId),
      ]);
      setEntries(t || []); setMatters(m || []);
        } finally {
          setLoading(false);
        }
      })();
  }, [firmId, refreshSignal]);

  const kpis = useMemo(() => {
    const today = new Date().toISOString().slice(0,10);
    const todayEntries = entries.filter((e) => e.entry_date === today);
    const thisWeekStart = new Date();
    thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
    thisWeekStart.setHours(0,0,0,0);
    const weekEntries = entries.filter((e) => new Date(e.entry_date) >= thisWeekStart);
    const min = (rows) => rows.reduce((s, e) => s + (e.duration_minutes || 0), 0);
    return {
      todayMin: min(todayEntries),
      weekMin: min(weekEntries),
      weekFees: weekEntries.filter((e) => e.billable).reduce((s, e) => s + Number(e.amount || 0), 0),
    };
  }, [entries]);

  const startTimer = () => setTimer({ ...timer, running: true, startedAt: Date.now(), elapsed: 0 });
  const stopTimer = () => {
    const minutes = Math.max(6, Math.round(timer.elapsed / 60000));
    setForm({ ...blank, matter_id: timer.matter_id, description: timer.description, duration_minutes: minutes });
    setTimer({ running: false, startedAt: null, elapsed: 0, matter_id: '', description: '' });
    setShowNew(true);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      user_id: userProfile?.id || null,
      duration_minutes: Number(form.duration_minutes),
      hourly_rate: Number(form.hourly_rate),
      matter_id: form.matter_id || null,
    };
    const { data, error } = await createTimeEntry(firmId, payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    log('time', data.id, 'created', 'Time entry logged');
    setEntries((prev) => [data, ...prev]);
    setShowNew(false); setForm(blank);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Time Tracking</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Capture billable and non-billable work</p>
        </div>
        <Button icon={Plus} onClick={() => setShowNew(true)}>Log time</Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KPI label="Today"        value={formatDuration(kpis.todayMin)} icon={Clock} tone="indigo" />
        <KPI label="This week"    value={formatDuration(kpis.weekMin)} icon={Clock} tone="green" />
        <KPI label="Fees (week)"  value={formatMoney(kpis.weekFees)} tone="amber" />
      </div>

      {/* Timer */}
      <Card>
        {/* Timer composer — stack vertically on mobile so each control
            gets full width; flex-wrap horizontally on sm+. The original
            had a fixed w-64 Select that forced an awkward two-line
            wrap on phones. */}
        <div className="p-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
          <Select className="w-full sm:w-64" placeholder="Select matter…"
            options={matters.map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` }))}
            value={timer.matter_id} onChange={(e) => setTimer({ ...timer, matter_id: e.target.value })} />
          <Input className="w-full sm:flex-1 sm:min-w-[200px]" placeholder="What are you working on?"
            value={timer.description} onChange={(e) => setTimer({ ...timer, description: e.target.value })} />
          <div className="font-mono text-lg">
            {formatDuration(Math.floor(timer.elapsed / 60000))}
          </div>
          {timer.running ? (
            <Button variant="danger" icon={Pause} onClick={stopTimer}>Stop & log</Button>
          ) : (
            <Button variant="success" icon={Play} onClick={startTimer} disabled={!timer.matter_id}>Start timer</Button>
          )}
        </div>
      </Card>

      <Card>
        {loading ? <SkeletonListRows rows={6} /> : !entries.length ? (
          <EmptyState
            icon={Clock}
            title="Start tracking your time"
            description="Even on flat-fee work, tracking billable time tells you whether the fee is profitable. Use the timer at the top, or click ‘Log time' to enter a past entry."
            action={<Button icon={Plus} onClick={() => setShowNew(true)}>Log time</Button>}
          />
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {entries.map((e) => (
                <li key={e.id} className="px-4 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{e.matter?.matter_number || '—'}</span>
                      <Badge tone={e.billable ? 'green' : 'slate'}>{e.billable ? 'Billable' : 'Non-bill.'}</Badge>
                    </div>
                    {e.description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{e.description}</p>}
                    <p className="text-xs text-slate-400 mt-0.5">{formatDate(e.entry_date)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-mono font-semibold">{formatDuration(e.duration_minutes)}</p>
                    <p className="text-xs text-slate-500 font-mono">{formatMoney(e.amount || 0)}</p>
                  </div>
                </li>
              ))}
            </ul>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">Date</th>
                    <th className="text-left px-6 py-2 font-medium">Case File</th>
                    <th className="text-left px-6 py-2 font-medium">Description</th>
                    <th className="text-right px-6 py-2 font-medium">Duration</th>
                    <th className="text-right px-6 py-2 font-medium">Rate</th>
                    <th className="text-right px-6 py-2 font-medium">Amount</th>
                    <th className="text-left px-6 py-2 font-medium">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {entries.map((e) => (
                    <tr key={e.id}>
                      <td className="px-6 py-2.5">{formatDate(e.entry_date)}</td>
                      <td className="px-6 py-2.5">
                        <p className="font-medium">{e.matter?.matter_number || '—'}</p>
                        <p className="text-xs text-slate-500">{e.matter?.title}</p>
                      </td>
                      <td className="px-6 py-2.5 max-w-md truncate">{e.description || '—'}</td>
                      <td className="px-6 py-2.5 text-right font-mono">{formatDuration(e.duration_minutes)}</td>
                      <td className="px-6 py-2.5 text-right text-slate-500">{formatMoney(e.hourly_rate || 0)}/h</td>
                      <td className="px-6 py-2.5 text-right font-mono">{formatMoney(e.amount || 0)}</td>
                      <td className="px-6 py-2.5"><Badge tone={e.billable ? 'green' : 'slate'}>{e.billable ? 'Billable' : 'Non-billable'}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="Log time" size="lg" footer={
        <>
          <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
          <Button loading={saving} onClick={submit}>Save entry</Button>
        </>
      }>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label required>Date</Label><Input type="date" required value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} /></div>
          <div><Label required>Case File</Label><Select required placeholder="Select…"
            options={matters.map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` }))}
            value={form.matter_id} onChange={(e) => setForm({ ...form, matter_id: e.target.value })} /></div>
          <div><Label required>Duration (minutes)</Label><Input type="number" min="6" step="6" required value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></div>
          <div><Label>Hourly rate</Label><Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} /></div>
          <div><Label>Activity code</Label><Input value={form.activity_code} onChange={(e) => setForm({ ...form, activity_code: e.target.value })} placeholder="e.g. L110 — Fact Investigation" /></div>
          <div>
            <Label>Type</Label>
            <Select options={[
              { value: 'true', label: 'Billable' },
              { value: 'false', label: 'Non-billable' },
            ]} value={String(form.billable)} onChange={(e) => setForm({ ...form, billable: e.target.value === 'true' })} />
          </div>
          <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        </form>
      </Modal>
    </div>
  );
}
